import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { KeycloakEventTypeLegacy, KeycloakService } from 'keycloak-angular';
import { firstValueFrom } from 'rxjs';
import { UserModuleAccessService } from '@shared-lib/foundry-shared/user-module-access.service';
import { EntityPermissionRoleService } from '@shared-lib/foundry-shared/entity-permission-role.service';
import { environment } from '../../../environments/environment';
import { SessionExpirationService } from './session-expiration.service';

@Injectable({
  providedIn: 'root',
})
export class KeycloakInitService {
  private permissionsLoaded = false;
  private permissionsLoadPromise: Promise<void> | null = null;
  private monitoringAuthEvents = false;

  constructor(
    private keycloakService: KeycloakService,
    private router: Router,
    private http: HttpClient,
    private userModuleAccess: UserModuleAccessService,
    private entityPermissionRole: EntityPermissionRoleService,
    private sessionExpiration: SessionExpirationService,
  ) {}
  async initKeycloak(): Promise<void> {
    try {
      this.monitorAuthEvents();
      await this.keycloakService.init({
        config: {
          url: environment.KEYCLOAK_URL,
          realm: environment.KEYCLOAK_REALM,
          clientId: environment.KEYCLOAK_CLIENT_ID,
        },
        initOptions: {
          onLoad: 'check-sso',
          checkLoginIframe: false,
          silentCheckSsoRedirectUri:
            window.location.origin + '/assets/silent-check-sso.html',
          pkceMethod: 'S256',
        },
        enableBearerInterceptor: true,
        bearerPrefix: 'Bearer',
      });

      this.logAuthenticatedRoles();

      if (this.keycloakService.isLoggedIn()) {
        await this.loadAuthenticatedUserPermissions();
        return Promise.resolve();
      }
    } catch (error) {
      console.error('Keycloak initialization failed', error);
      throw error;
    }
  }

  private monitorAuthEvents(): void {
    if (this.monitoringAuthEvents) return;
    this.monitoringAuthEvents = true;
    this.keycloakService.keycloakEvents$.subscribe((event) => {
      if (event.type === KeycloakEventTypeLegacy.OnTokenExpired) {
        this.sessionExpiration.showExpiredPage('token-expired');
      } else if (event.type === KeycloakEventTypeLegacy.OnAuthRefreshError) {
        this.sessionExpiration.showExpiredPage('refresh-failed');
      } else if (
        event.type === KeycloakEventTypeLegacy.OnAuthSuccess
        || event.type === KeycloakEventTypeLegacy.OnAuthRefreshSuccess
      ) {
        this.sessionExpiration.reset();
      }
    });
  }

  loadAuthenticatedUserPermissions(): Promise<void> {
    if (this.permissionsLoaded) return Promise.resolve();
    if (this.permissionsLoadPromise) return this.permissionsLoadPromise;

    this.permissionsLoadPromise = this.fetchAuthenticatedUserPermissions()
      .then((loaded) => {
        this.permissionsLoaded = loaded;
      })
      .finally(() => {
        this.permissionsLoadPromise = null;
      });
    return this.permissionsLoadPromise;
  }

  private async fetchAuthenticatedUserPermissions(): Promise<boolean> {
    this.userModuleAccess.clear();
    const role = this.entityPermissionRole.getLoggedInRole();
    if (!role) {
      console.warn('[USER_MODULE_PERMISSIONS_SKIPPED] No application role was available for the logged-in user');
      return false;
    }

    const baseUrl = environment.URL.replace(/\/$/, '');
    const url = `${baseUrl}/spriced/auth/getEntityPermissions`;
    try {
      console.info('[USER_MODULE_PERMISSIONS_LOAD_STARTED]', { url, userRole: role });
      const response = await firstValueFrom(
        this.http.post<unknown>(
          url,
          { userRole: role },
        ),
      );
      this.userModuleAccess.setFromPermissionResponse(response);
      console.info('[USER_MODULE_PERMISSIONS_LOAD_COMPLETED]', {
        modules: this.userModuleAccess.getAllowedModules(),
      });
      return true;
    } catch (error) {
      // Fail closed: no permission response means no protected module cards.
      this.userModuleAccess.clear();
      console.error('[USER_MODULE_PERMISSIONS_LOAD_FAILED]', error);
      return false;
    }
  }

  private logAuthenticatedRoles(): void {
    const keycloak = this.keycloakService.getKeycloakInstance();
    const claims = keycloak?.tokenParsed as any;
    const realmRoles = Array.isArray(claims?.realm_access?.roles)
      ? claims.realm_access.roles.map((role: unknown) => String(role))
      : [];
    const clientRoles = Object.fromEntries(
      Object.entries((claims?.resource_access || {}) as Record<string, any>)
        .map(([clientId, access]) => [
          clientId,
          Array.isArray(access?.roles) ? access.roles.map((role: unknown) => String(role)) : [],
        ]),
    );
    let resolvedRoles: string[] = [];
    try {
      resolvedRoles = this.keycloakService.getUserRoles(true) || [];
    } catch {
      resolvedRoles = [];
    }

    console.info('[AUTH_ROLE_TRACE][KEYCLOAK_INIT]', {
      authenticated: this.keycloakService.isLoggedIn(),
      username: String(claims?.preferred_username || claims?.username || ''),
      realmRoles,
      clientRoles,
      resolvedRoles,
    });
  }
}
